import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

import HeroImg from './icons/hero-img/index.vue'
import IndividualFirstFont from './components/individual-first-font'
import FoundrySelect from './components/foundry-select'
import DateSelectDropdown from './components/date-select-dropdown'
import ArticlesAndVideos from './components/articles-and-videos'
import MerchendisingTips from './components/merchendising-tips'
import TypeTrends from './components/type-trends'
import AnimationProcessing from 'components/animations/processing'
import Promotions from './components/promotions'
import Bundles from './components/bundles'
import EarningReport from './components/earning-report'
import BlurReport from './components/earning-order-report-blur'
import BlurPromotions from './components/promotions-blur'
import BlurBundles from './components/bundles-blur'
import BlurDateFilter from './components/date-filter-blur'
import BlurCms from './components/articles-and-vedios-blur'
import Ufda2Agreement from './components/ufda2-agreement'
import Misc from 'src/utils/helper/misc'
import { DATE_RANGE_OPTIONS, DATE_RANGE_DROPDOWN_DEFAULT_DAYS } from 'src/utils/constants/common'
import { isEmpty, isEqual } from 'lodash'
import FoundryHealth from './components/foundry-health';
import FouSidebar from 'components/overlay/fou-side-overlay'
import SidebarContent from './components/sidebar-content'
import TopPerformingFamilies from './components/top-performing-families'
import BlurTopPerformingFamilies from './components/top-performing-families-blur'
import QuestionMarkInfoIcon from './icons/foundry-health/question-mark-info/index.vue'
import { MdIconArrowCaret, MdTypography } from '@antiqua-dls/vue-components'
import FoundryBannerSection from './components/foundry-banner'
import SignAgreementModal from 'components/general/small-popup-info-modal'
import { useRouter } from 'vue-router'
import { adobeCjaEventEmitter } from '../../../../modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_CATEGORIES, LINK_SECTION, LINK_NAMES, LINK_TYPES } from '../../../../modules/adobe-cja/constants';

import FouEmailVerify from '../../components/general/fou-email-verify';
import { isTerminatingOrTerminated } from 'src/utils/helper/contractTermination'

export default{
  components:{
    HeroImg,
    IndividualFirstFont,
    FoundrySelect,
    DateSelectDropdown,
    ArticlesAndVideos,
    MerchendisingTips,
    TypeTrends,
    AnimationProcessing,
    Promotions,
    Bundles,
    EarningReport,
    BlurReport,
    BlurPromotions,
    BlurBundles,
    BlurDateFilter,
    Ufda2Agreement,
    BlurCms,
    TopPerformingFamilies,
    BlurTopPerformingFamilies,
    FoundryHealth,
    FouSidebar,
    SidebarContent,
    QuestionMarkInfoIcon,
    MdIconArrowCaret,
    MdTypography,
    FoundryBannerSection,
    SignAgreementModal,
    FouEmailVerify
  },
  setup(){
    const store = useStore()
    const router = useRouter()

    const showTopSec = ref(true)
    const showTopPerformingSec = ref(true)
    const showPromotionsSec = ref(true)
    const showArticlesSec = ref(true)
    const showBundlesSec = ref(true)
    const showSmallAside = ref(false)
    const aSideSection = ref(null)
    const smallScreenFouLogoWrapper = ref(null)
    const smallScreenFouFirstLetter = ref(null)
    const smallScreenFouImg = ref(null)
    const submissionCounting = ref(false)
    const foundryLogoUrl = ref(false)
    const isEarningReport = computed(() => isEqual(store.getters['dashboard/getReportType'], 'earnings'))
    const bannerURL = computed(() => store.getters['profile/getBannerURL'])
    const logoURL = computed(() => store.getters['profile/getLogoURL'])

    const dateRangeDays = computed(() => store.getters['dashboard/getDateRangeDays'])
    const hasNoSubmission = computed(() => store.getters['submissions/hasNoSubmission'])
    const currentFoundryName = computed(() => store.getters['profile/sessionFoundryName'])
    const loading = computed(() => store.getters['profile/loadingFoundrySpinner'])
    const loadingReport = computed(() => store.getters['dashboard/getLoadingReport'])
    const currentFoundryInitial = computed(() => store.getters['profile/sessionFoundryName']?.split(' ').map(word => word.charAt(0)).join('') || '');
    const currentFoundryId = computed(() => store.getters['profile/sessionFoundryId'])
    const allFoundries = computed(() => store.getters['profile/foundries'])
    const foundriesList = computed(() => allFoundries.value?.filter((foundry) => foundry.permissions.length > 0))
    const submissionCountingStatus = computed(() => store.getters['submissions/getSubmissionCountingStatus'])
    const ufda2Status = computed(() => store.getters['dashboard/getUfda2Status'])

    const isSessionFoundryNonActive = computed(() => {
      const foundry = allFoundries.value?.find(f => f.id === currentFoundryId.value)
      return isTerminatingOrTerminated(foundry)
    })

    const dashboardClass = computed(() => ({ 'overlay': showSmallAside.value, 'fou-dashboard-page--non-ufda2': !ufda2Status.value }))
    const user = computed(() => store.getters['auth/user'])
    const getLogoutState = computed(() => store.getters['auth/getLogoutState'])
    const getHealthReportChallenge = computed(() => store.getters['healthReport/getHealthReportChallenge'])
    const contractType = ref('')
    const sessionFoundryIsAdmin = ref(false)
    const isSideBarOpen = ref(false)
    const maxScore = computed(() => store.getters['healthReport/getMaxScore'])
    const visibilityScore = computed(() => store.getters['healthReport/getVisibilityScore'])
    const placeFoundryHealthAfterReport = computed(() => visibilityScore.value >= maxScore.value)
    const showFoundryHealthSection = ref( maxScore.value > visibilityScore.value );

    const hasSubmissionPermission = computed(() => {
      return Misc.foundryHasModulePermission('submissions', foundriesList.value, currentFoundryId.value)
    })

    const scrollToArticles = computed(() => {
      return store.getters['dashboard/getScrollToArticles']
    })

    const hasFoundryProfilePermission = computed(() => {
      return Misc.foundryHasModulePermission('Foundry Profile', foundriesList.value, currentFoundryId.value)
    })

    const hasSalesReportPermission = computed(() =>{
      return Misc.foundryHasModulePermission('salesReport', foundriesList.value, currentFoundryId.value)
    })

    const hasAgreementsPermission = computed(() => {
      return Misc.foundryHasModulePermission('agreements', foundriesList.value, currentFoundryId.value)
    })

    const hasPromotionPermission = computed(() => {
      return Misc.foundryHasModulePermission('promotions', foundriesList.value, currentFoundryId.value)
    })

    const hasBundlesPermission = computed(() => {
      return Misc.foundryHasModulePermission('bundles', foundriesList.value, currentFoundryId.value)
    })

    const promotionList = computed(() => {
      return store.getters['promotions/promotionsList']
    })

    const bundlesList = computed(() => {
      return store.getters['bundle/getBundleLive']
    })

    const showAgreementModal = computed(() => {
      return store.getters['dashboard/getShowAgreementModal']
    })

    /**
     * User can see welcome hero section if hasSignedUfda2 AND notCountingSubmissions AND ( notFontSubmittedYet OR noSalesReportPermission )
     * @type {boolean}
    */
    const showHeroWelcomeSection = computed(() => {
      return ufda2Status.value
        && ( !hasSalesReportPermission.value
          || (!hasSalesReportPermission.value && !hasSubmissionPermission.value)
          || (hasSubmissionPermission.value && !submissionCountingStatus.value && hasNoSubmission.value)
        )
        && !submissionCounting.value
    })

    const getImpersonateUser = computed(() => {
      if (user.value?.impersonate) {
        return user.value?.impersonate
      }
      const fetchFSValue = JSON.parse(window.localStorage.getItem('FS-Impersonate'))
      return fetchFSValue && !getLogoutState.value
    })

    /**
     * User can see upload first font section if hasSignedUfda2 AND noSubmissionPermission AND notCountingSubmissions AND notFontSubmittedYet
     * @type {boolean}
    */
    const showUploadFirstSection = computed(() => {
      return ufda2Status.value && hasSubmissionPermission.value
      && !submissionCountingStatus.value && hasNoSubmission.value
    })

    /**
     * User can see report section if hasSignedUfda2 AND notCountingSubmissions AND hasFontSubmitted AND hasSalesReportPermission AND reportDataIsNotLoading
     * @type {boolean}
    */
    const showReportSection = computed(() => {
      return !submissionCountingStatus.value
      && hasSalesReportPermission.value
        && !showHeroWelcomeSection.value
    })

    const showReportLoader = computed( () => {
      return !loading.value && loadingReport.value &&
        (showHeroWelcomeSection.value || !showReportSection.value) && (!showHeroWelcomeSection.value || showReportSection.value)
    })

    const manageFoundryLink = computed( () => {
      return `/edit-foundry/${currentFoundryId.value}`
    })

    const getSubmissionCount = async (foundryId) => {
      if (hasSalesReportPermission.value) {
        submissionCounting.value = true
        await store.dispatch('submissions/getInReviewOrApprovedOrLiveSubmissionCount', { foundryId: foundryId })
        submissionCounting.value = false
      }
    }

    const getEarningOrderPerformerData = async () => {
      if (hasSalesReportPermission.value) {
        await store.dispatch('dashboard/updateDateRangeDays', dateRangeDays.value || DATE_RANGE_DROPDOWN_DEFAULT_DAYS)
      }
    }
    
    const showArticlesSection = () => {
      showArticlesSec.value = !showArticlesSec.value
    }

    const showPromotionsSection = () => {
      showPromotionsSec.value = !showPromotionsSec.value
    }

    const showTopSection = () => {
      showTopSec.value = !showTopSec.value
    }

    const toggleFoundryHealthSection = () => {
      showFoundryHealthSection.value = !showFoundryHealthSection.value
    }

    watch([maxScore, visibilityScore], ([newMaxScore, newVisibilityScore]) => {
      showFoundryHealthSection.value = newMaxScore > newVisibilityScore;
    });

    const showTopPerformaingFamiliesSection = () => {
      showTopPerformingSec.value = !showTopPerformingSec.value
    }

    const showBundlesSection = () => {
      showBundlesSec.value = !showBundlesSec.value
    }

    const toggleSmallScreenAside = () => {
      showSmallAside.value = !showSmallAside.value
    }

    const changeEarningType =(type)=> {
      adobeCjaEventEmitter({
        event: CJA_EVENTS.LINK_CLICK,
        links: {
          linkCategory: LINK_CATEGORIES.REPORT_DASHBOARD,
          linkSection: LINK_SECTION.DASHBOARD,
          linkName: type === 'earnings' ? LINK_NAMES.EARNINGS : LINK_NAMES.ORDERS,
          linkType: LINK_TYPES.BUTTON
        }
      })
      store.commit('dashboard/setReportType',type)
      if(isEqual('earnings', type)){
        store.dispatch('dashboard/modifiedEarningGraphData')
      }else{
        store.dispatch('dashboard/modifiedOrderGraphData')
      }
    }
    const handleClickOutside = (event) => {
      const isSmallScreenFouLogoWrapper = isEqual(smallScreenFouLogoWrapper.value, event.target) // check if event.target is smallScreenFouLogo
      const isSmallScreenFouLogo = isEqual(smallScreenFouFirstLetter.value, event.target) || isEqual(smallScreenFouImg.value, event.target) // check if event.target is smallScreenFouFirstLetter and smallScreenFouImg
      const isAside = aSideSection.value?.contains(event.target) // check if event.target is triggered inside <asdie> section
      if ( !isSmallScreenFouLogoWrapper && !isSmallScreenFouLogo && !isAside && showSmallAside.value ) {
        toggleSmallScreenAside()
      }
    }

    const getFoundryLogo = (foundryId) => {
      store.commit('dashboard/setFoundryLogo', null)
      store.commit('dashboard/setFoundryPoster', null)
      let foundrylogo = foundriesList.value.find(foundry => isEqual(foundry.id, foundryId))
      foundryLogoUrl.value = foundrylogo.logoUrl
    }

    const setUfda2Status = () => {
      const selectedFoundry = allFoundries.value?.find(foundry => isEqual(foundry.id, currentFoundryId.value))
      sessionFoundryIsAdmin.value = selectedFoundry?.isAdmin === true
      const hasUfda2 = isEqual(selectedFoundry?.contractSummary,'UFDA 2')
      contractType.value = selectedFoundry?.contractSummary
      store.commit('dashboard/setUfda2Status', hasUfda2)
    }

    const scrollToTop = () => {
      window.scrollTo(0, 0)
    }
    
    let savedScrollTop = 0
    const toggleSideBar = () => {
      const root = document.getElementById('app')
      if (!isSideBarOpen.value) {
        savedScrollTop = root.scrollTop || window.scrollY
      }
      isSideBarOpen.value = !isSideBarOpen.value
      adobeCjaEventEmitter({
        event: isSideBarOpen.value ? CJA_EVENTS.OVERLAY_LOAD : CJA_EVENTS.OVERLAY_CLOSE,
        content: {
          overlay: ''
        }
      })
      nextTick(() => {
        root.scrollTop = savedScrollTop
        window.scrollTo(0, savedScrollTop)
      })
    }

    const getFoundryHealthChallenges = async () => {
      await store.dispatch('healthReport/getFoundryHealthChallenges', { foundryId: currentFoundryId.value })
    }

    const navigateToAgreementSection = () => {
      closeSignAgreementModal()
      router.push({ name: 'contract' })
    }

    const closeSignAgreementModal = () => {
      store.commit('dashboard/setShowAgreementModal', false)
    }

    watch(currentFoundryId, async (foundryId) => {
      if (!isEmpty(foundryId)) {
        scrollToTop()
        setUfda2Status()
        await getSubmissionCount(foundryId)
        getFoundryLogo(foundryId)
        await getEarningOrderPerformerData()
        getFoundryHealthChallenges()
        showTopSec.value = true
        showTopPerformingSec.value = true
        showPromotionsSec.value = true
        showArticlesSec.value = true
        showBundlesSec.value = true
      }
    })

    onMounted(async () => {
      const currentDate = new Date();
      const start = new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), currentDate.getDate());
      let startDate = Misc.formatDate(new Date(start), 'en', 'YYYY-MM-DD')
      let endDate = Misc.formatDate(new Date(currentDate), 'en', 'YYYY-MM-DD')
      store.commit('dashboard/setStartDate', startDate)
      store.commit('dashboard/setEndDate', endDate)
      scrollToTop()
      store.commit('profile/clearFoundryData')
      await store.dispatch('profile/getFoundries')
      window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(allFoundries.value)))
      store.dispatch('auth/setPermissionsArr', Misc.getPermissionsArr(allFoundries.value))
      setUfda2Status()
      window.addEventListener('click', handleClickOutside)
      store.commit('dashboard/setReportType', 'earnings');
    })

    onUnmounted(() => {
      window.removeEventListener('click', handleClickOutside)
    })

    watch(scrollToArticles, (newVal) => {
      if (newVal) {
        showArticlesSec.value = true
        store.commit('dashboard/setScrollToArticles', false)
      }
    })

    return{
      hasBundlesPermission,
      hasPromotionPermission,
      bundlesList,
      showBundlesSection,
      showBundlesSec,
      promotionList,
      showArticlesSec,
      showArticlesSection,
      showPromotionsSection,
      showPromotionsSec,
      showTopPerformaingFamiliesSection,
      showTopPerformingSec,
      showTopSection,
      showTopSec,
      contractType,
      currentFoundryName,
      loading,
      currentFoundryInitial,
      hasFoundryProfilePermission,
      showReportSection,
      foundryLogoUrl,
      changeEarningType,
      showSmallAside,
      toggleSmallScreenAside,
      isEarningReport,
      hasNoSubmission,
      submissionCountingStatus,
      DATE_RANGE_OPTIONS,
      showHeroWelcomeSection,
      dateRangeDays,
      getSubmissionCount,
      loadingReport,
      ufda2Status,
      isSessionFoundryNonActive,
      sessionFoundryIsAdmin,
      smallScreenFouLogoWrapper,
      smallScreenFouImg,
      smallScreenFouFirstLetter,
      aSideSection,
      hasAgreementsPermission,
      showUploadFirstSection,
      dashboardClass,
      showReportLoader,
      manageFoundryLink,
      submissionCounting,
      getImpersonateUser,
      getEarningOrderPerformerData,
      handleClickOutside,
      hasSalesReportPermission,
      isSideBarOpen,
      toggleSideBar,
      getHealthReportChallenge,
      showFoundryHealthSection,
      toggleFoundryHealthSection,
      placeFoundryHealthAfterReport,
      maxScore,
      visibilityScore,
      bannerURL,
      logoURL,
      showAgreementModal,
      navigateToAgreementSection,
      closeSignAgreementModal,
    }
  }
}